import axios from 'axios';
import { API_CONFIG } from '../config/api.config';

// Create axios instance
const apiClient = axios.create({
  baseURL: API_CONFIG.baseURL,
  headers: {
    'Content-Type': 'multipart/form-data',
  },
});

// Get token function, returns token after successful verification
export const getToken = async (captchaToken = null) => {
  try {
    const response = await apiClient.post(API_CONFIG.endpoints.getToken, {
      captchaToken,
    });
    return response.data.token;  // Return the token from the API response
  } catch (error) {
    console.error("Error fetching token:", error);
    throw error; // Rethrow error if fetching token fails
  }
};

// Flag to prevent multiple refreshes at once
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Axios response interceptor to catch 401 errors (unauthorized)
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If token refresh is in progress, queue up the request and retry once the new token is available
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return apiClient(originalRequest); // Retry the original request
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true; // Mark request as retried
      isRefreshing = true;

      try {
        // Get the captcha token from sessionStorage
        const cachedCaptchaToken = sessionStorage.getItem('captchaToken') || null;
        const newToken = await getToken(cachedCaptchaToken);  // Fetch new token

        // Set the new token in the default headers
        apiClient.defaults.headers.common['Authorization'] = 'Bearer ' + newToken;
        processQueue(null, newToken);  // Resolve queued requests with new token

        return apiClient(originalRequest);  // Retry the original request with the new token
      } catch (err) {
        processQueue(err, null);
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error); // If 401 not triggered or other error occurred
  }
);

// API function to submit the supplier form
export const submitSupplierForm = async (formData) => {
  const formDataToSend = new FormData();
  Object.keys(formData).forEach((key) => {
    if (key !== 'captchaToken') {
      formDataToSend.append(key, formData[key]);
    }
  });

  try {
    // First, fetch the token before making the request
    const captchaToken = formData.captchaToken; // Get captchaToken from form data
    const token = await getToken(captchaToken);  // Get the token using the captcha token

    // Now make the actual API request with the fetched token
    const response = await apiClient.post(API_CONFIG.endpoints.supplier, formDataToSend, {
      headers: {
        Authorization: `Bearer ${token}`, // Pass the token in the header
      },
    });

    return response.data; // Return the response from the API
  } catch (error) {
    console.error("Error submitting supplier form:", error);
    throw error; // Rethrow error to be handled by the caller
  }
};


export const submitCareerForm = async (formData) => {
  const formDataToSend = new FormData();
  Object.keys(formData).forEach((key) => {
    if (key !== 'captchaToken') {
      formDataToSend.append(key, formData[key]);
    }
  });

  try {
    // First, fetch the token using the captchaToken
    const captchaToken = formData.captchaToken;
    const token = await getToken(captchaToken);

    // Make the API request with the token in the Authorization header
    const response = await apiClient.post(API_CONFIG.endpoints.career, formDataToSend, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error submitting career form:", error);
    throw error;
  }
};